import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color, num, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSheetValue } from '#hooks/useSheetValue';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';
import { envelopeBudget } from '#spreadsheet/bindings';

import {
  extraCobrada,
  pagaDelMes,
  pagaRecienCobrada,
  recortarReparto,
  sinDecidir,
  totalPlan,
} from './nomina';
import type { CobroPaga, ConfigNomina, PlanPaga } from './nomina';
import { repartirPaga } from './repartir';
import { nombrePaga } from './textos';
import {
  useConfigNomina,
  useIngresosDelMes,
  usePlanesPagas,
} from './useNomina';

/**
 * El día que llega una paga extra (maqueta 2): en Inicio, en cuanto llega
 * el dinero (aunque sea antes del día previsto) o durante los 15 días
 * siguientes a la fecha de cobro, y mientras no se haya repartido ni dicho
 * «Ahora no», Gavi la anuncia y ofrece repartirla (según lo decidido, si ya
 * se decidió algo).
 */
export function AvisoPagaLlegada() {
  const { config, pagador } = useConfigNomina();
  const { planes } = usePlanesPagas();
  const [cerrada, setCerrada] = useState(false);
  const hoy = monthUtils.currentDay();
  const ingresosMes = useIngresosDelMes(hoy.slice(0, 7), pagador);
  if (!config || config.neta <= 0 || cerrada) {
    return null;
  }
  const delMes = pagaDelMes(config, hoy, ingresosMes);
  const cobro = delMes?.llego ? delMes.cobro : pagaRecienCobrada(config, hoy);
  if (!cobro) {
    return null;
  }
  const plan = planes[cobro.clave];
  if (plan && plan.estado !== 'pendiente') {
    return null;
  }
  return (
    <SheetNameProvider name={monthUtils.sheetForMonth(cobro.fecha.slice(0, 7))}>
      <HojaPaga
        config={config}
        pagador={pagador}
        cobro={cobro}
        plan={plan}
        onCerrar={() => setCerrada(true)}
      />
    </SheetNameProvider>
  );
}

function HojaPaga({
  config,
  pagador,
  cobro: prevista,
  plan,
  onCerrar,
}: {
  config: ConfigNomina;
  pagador: string | null;
  cobro: CobroPaga;
  plan: PlanPaga | undefined;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { guardarPlan } = usePlanesPagas();
  const { data: { list: categorias } = { list: [] } } = useCategories();
  const [repartiendo, setRepartiendo] = useState(false);
  const mes = prevista.fecha.slice(0, 7);
  const ingresos = useIngresosDelMes(mes, pagador);
  const porAsignar =
    useSheetValue<'envelope-budget', 'to-budget'>(envelopeBudget.toBudget) ?? 0;
  const nombre = nombrePaga(t, prevista.paga, locale);
  // Llegó si del pagador entró más que la nómina; entonces manda lo que
  // llegó de verdad, no lo previsto (que sale del año pasado).
  const extra = extraCobrada(config, ingresos);
  const llego = extra != null;
  const cobro = llego ? { ...prevista, importe: extra } : prevista;
  const destinos = recortarReparto(plan?.destinos ?? [], porAsignar);
  const aRepartir = destinos.reduce((s, d) => s + d.importe, 0);
  const recortado = aRepartir < totalPlan(plan);

  const ahoraNo = () => {
    guardarPlan(cobro.clave, {
      destinos: plan?.destinos ?? [],
      estado: 'ahora-no',
    });
    onCerrar();
  };

  const repartir = async () => {
    if (!plan || aRepartir <= 0) {
      return;
    }
    setRepartiendo(true);
    try {
      await repartirPaga(mes, destinos);
      guardarPlan(cobro.clave, { ...plan, estado: 'repartida' });
      dispatch(
        addNotification({
          notification: {
            type: 'message',
            message: t('Payment shared out: {{amount}}', {
              amount: format(aRepartir, 'financial'),
            }),
          },
        }),
      );
      onCerrar();
    } finally {
      setRepartiendo(false);
    }
  };

  const nombreDestino = (d: PlanPaga['destinos'][number]) =>
    d.tipo === 'por-asignar'
      ? t('Ready to Assign')
      : (categorias.find(c => c.id === d.id)?.name ?? t('Deleted category'));

  const sinPlan = !plan || plan.destinos.length === 0;
  const falta = sinDecidir(cobro.importe, plan);

  return (
    <Hoja
      aria-label={t('Your {{name}}', { name: nombre })}
      data-testid="paga-llegada"
      onCerrar={onCerrar}
    >
      <View style={{ gap: 14, paddingTop: 6 }}>
        <Bocadillo
          variante="hoja"
          estado="aplaude"
          texto={
            llego
              ? t('High tide! Your {{name}} has arrived.', {
                  name: nombre,
                })
              : t('Has your {{name}} arrived yet?', { name: nombre })
          }
        >
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            <PrivacyFilter>
              {sinPlan
                ? t('{{amount}}. Shall we decide what it does?', {
                    amount: format(cobro.importe, 'financial'),
                  })
                : t('{{amount}}. Shall we share it out as you planned?', {
                    amount: format(cobro.importe, 'financial'),
                  })}
            </PrivacyFilter>
          </Text>
        </Bocadillo>

        {!sinPlan && plan && (
          <View
            style={{
              borderRadius: 16,
              border: `1px solid ${color.line}`,
              overflow: 'hidden',
            }}
          >
            {plan.destinos.map((d, i) => (
              <View
                key={d.tipo === 'categoria' ? d.id : d.tipo}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderTop: i === 0 ? 0 : `1px solid ${color.line}`,
                }}
              >
                <Text style={{ ...texto.cuerpo, fontWeight: 600 }}>
                  {nombreDestino(d)}
                </Text>
                <Text style={{ ...texto.cuerpo, ...num, fontWeight: 700 }}>
                  <PrivacyFilter>
                    {format(d.importe, 'financial')}
                  </PrivacyFilter>
                </Text>
              </View>
            ))}
            {falta > 0 && (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderTop: `1px solid ${color.line}`,
                  backgroundColor: color.warnSoft,
                }}
              >
                <View>
                  <Text style={{ ...texto.cuerpo, fontWeight: 600 }}>
                    <Trans>Undecided</Trans>
                  </Text>
                  <Text style={{ ...texto.pequeno, color: color.warn }}>
                    <Trans>Stays in To assign</Trans>
                  </Text>
                </View>
                <Text
                  style={{
                    ...texto.cuerpo,
                    ...num,
                    fontWeight: 700,
                    color: color.warn,
                  }}
                >
                  <PrivacyFilter>{format(falta, 'financial')}</PrivacyFilter>
                </Text>
              </View>
            )}
          </View>
        )}

        {recortado && aRepartir > 0 && (
          <Text style={{ ...texto.pequeno, color: color.warn }}>
            <PrivacyFilter>
              {t(
                'Only {{amount}} is left to assign, so that is what will be shared out.',
                { amount: format(aRepartir, 'financial') },
              )}
            </PrivacyFilter>
          </Text>
        )}

        {sinPlan ? (
          <Boton
            bloque
            data-testid="paga-llegada-decidir"
            onPress={() => {
              onCerrar();
              void navigate(`/pagas/${encodeURIComponent(cobro.clave)}`);
            }}
          >
            <Trans>Share it out</Trans>
          </Boton>
        ) : (
          <Boton
            bloque
            data-testid="paga-llegada-repartir"
            isDisabled={repartiendo || aRepartir <= 0}
            onPress={() => void repartir()}
          >
            <PrivacyFilter>
              {t('Share out {{amount}}', {
                amount: format(aRepartir, 'financial'),
              })}
            </PrivacyFilter>
          </Boton>
        )}
        <View style={{ flexDirection: 'row', gap: 10 }}>
          {!sinPlan && (
            <Boton
              variante="fantasma"
              style={{ flex: 1 }}
              onPress={() => {
                onCerrar();
                void navigate(`/pagas/${encodeURIComponent(cobro.clave)}`);
              }}
            >
              <Trans>Change the plan</Trans>
            </Boton>
          )}
          <Boton
            variante="fantasma"
            style={{ flex: 1 }}
            data-testid="paga-llegada-ahora-no"
            onPress={ahoraNo}
          >
            <Trans>Not now</Trans>
          </Boton>
        </View>
      </View>
    </Hoja>
  );
}
